import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Intent } from '@blueprintjs/core';
import { ButtonSave } from 'components/buttons';
import { FormDateInput, FormSwitch, FormTextInput } from 'components/form-fields';
import { GridLayout } from 'components/grid';
import { ShowHide } from 'components/layout';
import { AlertConfirm, AlertUnsavedData, NotificationToaster } from 'components/notifications';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { useDirtyData } from 'hooks/useDirtyData';
import { PageRestricted } from 'pages/errors/page-restricted';
import { EmployeeService } from 'services';

// A single action covers reading and maintaining medical information - the users allowed to
// see it are the users who keep it up to date.
const requiredAction = "Employees:ViewSensitive";
const badRequestCode = 400;
const ohReferralMaxLength = 50;
const dateApiFormat = "YYYY-MM-DD";

// The backing columns are plain SQL DATE values with no range validation, and an assessment can
// legitimately be years in the past. FormDateInput otherwise defaults to a 30-months-back window
// and reports such dates as out of range.
const earliestSelectableDate = moment("1900-01-01").toDate();
const latestSelectableDate = moment("2100-01-01").toDate();

const medicalInformationModel = {
    lastHealthAssessmentDate: null,
    lastEyesightAssessmentDate: null,
    reportableMedicalCondition: false,
    ohReferral: "",
    ohReferralDate: null
};

// The API serialises dates with a time component, so they are normalised to the format the date
// fields write back. Without this, re-picking the date already shown would mark the form dirty.
function toDateValue(value) {
    if (value == null) {
        return null;
    }

    return moment(value).format(dateApiFormat);
}

// An assessment records something that has already happened.
function futureDateError(value, fieldDescription) {
    if (value == null) {
        return null;
    }

    if (moment(value, dateApiFormat).isAfter(moment(), "day")) {
        return `The ${fieldDescription} must be a date in the past.`;
    }

    return null;
}

function toFormModel(data) {
    return {
        lastHealthAssessmentDate: toDateValue(data?.lastHealthAssessmentDate),
        lastEyesightAssessmentDate: toDateValue(data?.lastEyesightAssessmentDate),
        reportableMedicalCondition: data?.reportableMedicalCondition ?? false,
        ohReferral: data?.ohReferral ?? "",
        ohReferralDate: toDateValue(data?.ohReferralDate)
    };
}

export function EmployeeMedical({ employeeId }) {
    const canAccessMedicalInformation = UserHasAccess(requiredAction);

    const [originalMedicalInformation, setOriginalMedicalInformation] = useState(medicalInformationModel);
    const [medicalInformation, setMedicalInformation] = useState(medicalInformationModel);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showClearOccupationalHealthConfirm, setShowClearOccupationalHealthConfirm] = useState(false);
    const isDirty = useDirtyData(originalMedicalInformation, medicalInformation);

    const hasOccupationalHealthDetails = medicalInformation.ohReferral !== ""
        || medicalInformation.ohReferralDate != null;

    let ohReferralError = null;
    if (medicalInformation.ohReferral.length > ohReferralMaxLength) {
        ohReferralError = `The occupational health referral cannot be longer than ${ohReferralMaxLength} characters.`;
    }

    const healthAssessmentDateError = futureDateError(
        medicalInformation.lastHealthAssessmentDate,
        "last health assessment date"
    );
    const eyesightAssessmentDateError = futureDateError(
        medicalInformation.lastEyesightAssessmentDate,
        "last eyesight assessment date"
    );
    const saveBlocked = ohReferralError != null
        || healthAssessmentDateError != null
        || eyesightAssessmentDateError != null;

    useEffect(onInit, [employeeId, canAccessMedicalInformation]);

    function onInit() {
        if (!canAccessMedicalInformation || employeeId == null) {
            return;
        }

        setLoading(true);

        EmployeeService.getMedicalInformation(employeeId).then(
            (data) => {
                const loaded = toFormModel(data);
                setMedicalInformation(loaded);
                setOriginalMedicalInformation(loaded);
                setLoading(false);
            },
            () => {
                // The form stays in its loading state so its empty values cannot be saved over the
                // stored record before the user reloads.
                NotificationToaster.show(Intent.DANGER, "Unable to load medical information. Please try again.");
            }
        );
    }

    function onFieldChange(field, value) {
        setMedicalInformation((previous) => {
            return {
                ...previous,
                [field]: value
            };
        });
    }

    function onDateFieldChange(field, item) {
        onFieldChange(field, item ? moment(item).format(dateApiFormat) : null);
    }

    function onReportableMedicalConditionChange(reportable) {
        // Dropping the condition discards the occupational health details with it, so the user
        // confirms before anything already recorded is lost.
        if (!reportable && medicalInformation.reportableMedicalCondition && hasOccupationalHealthDetails) {
            setShowClearOccupationalHealthConfirm(true);
            return;
        }

        onFieldChange("reportableMedicalCondition", reportable);
    }

    function onClearOccupationalHealthConfirm() {
        setShowClearOccupationalHealthConfirm(false);
        setMedicalInformation((previous) => {
            return {
                ...previous,
                reportableMedicalCondition: false,
                ohReferral: "",
                ohReferralDate: null
            };
        });
    }

    function onClearOccupationalHealthCancel() {
        setShowClearOccupationalHealthConfirm(false);
    }

    function saveMedicalInformation() {
        setSaving(true);

        // The endpoint replaces all five values, so an empty referral is sent as null to clear it.
        const request = {
            lastHealthAssessmentDate: medicalInformation.lastHealthAssessmentDate,
            lastEyesightAssessmentDate: medicalInformation.lastEyesightAssessmentDate,
            reportableMedicalCondition: medicalInformation.reportableMedicalCondition,
            ohReferral: medicalInformation.ohReferral === "" ? null : medicalInformation.ohReferral,
            ohReferralDate: medicalInformation.ohReferralDate
        };

        EmployeeService.saveMedicalInformation(employeeId, request).then(
            () => {
                setSaving(false);
                setOriginalMedicalInformation(medicalInformation);
                NotificationToaster.show(Intent.SUCCESS, "Medical information has been updated successfully.");
            },
            (error) => {
                setSaving(false);

                if (error?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Unable to save medical information. Please try again.");
                }
            }
        );
    }

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <div className="row">

                    <h1>Medical</h1>

                    <GridLayout numberOfColumns={2} gap={[0, 20]}>
                        <FormDateInput
                            id="medical-last-health-assessment-date"
                            headingText="Last Health Assessment"
                            value={medicalInformation.lastHealthAssessmentDate}
                            minDate={earliestSelectableDate}
                            maxDate={latestSelectableDate}
                            loading={loading}
                            disabled={loading || saving}
                            dangerHelperText={healthAssessmentDateError}
                            onChange={(item) => onDateFieldChange("lastHealthAssessmentDate", item)}
                        />
                        <FormDateInput
                            id="medical-last-eyesight-assessment-date"
                            headingText="Last Eyesight Assessment"
                            value={medicalInformation.lastEyesightAssessmentDate}
                            minDate={earliestSelectableDate}
                            maxDate={latestSelectableDate}
                            loading={loading}
                            disabled={loading || saving}
                            dangerHelperText={eyesightAssessmentDateError}
                            onChange={(item) => onDateFieldChange("lastEyesightAssessmentDate", item)}
                        />
                        <FormSwitch
                            id="medical-reportable-medical-condition"
                            headingText="Reportable Medical Condition"
                            checked={medicalInformation.reportableMedicalCondition}
                            checkedLabel="Yes"
                            uncheckedLabel="No"
                            loading={loading}
                            disabled={loading || saving}
                            onChange={(event) => onReportableMedicalConditionChange(event.target.checked)}
                        />
                    </GridLayout>

                    <ShowHide
                        evaluator={medicalInformation.reportableMedicalCondition}
                        show={(
                            <GridLayout numberOfColumns={2} gap={[0, 20]}>
                                <FormTextInput
                                    id="medical-oh-referral"
                                    headingText="Occupational Health Referral"
                                    value={medicalInformation.ohReferral}
                                    loading={loading}
                                    disabled={loading || saving}
                                    dangerHelperText={ohReferralError}
                                    onChange={(event) => onFieldChange("ohReferral", event.target.value)}
                                />
                                <FormDateInput
                                    id="medical-oh-referral-date"
                                    headingText="Occupational Health Referral Date"
                                    value={medicalInformation.ohReferralDate}
                                    minDate={earliestSelectableDate}
                                    maxDate={latestSelectableDate}
                                    loading={loading}
                                    disabled={loading || saving}
                                    onChange={(item) => onDateFieldChange("ohReferralDate", item)}
                                />
                            </GridLayout>
                        )}
                    />

                    <AlertConfirm
                        title="Please confirm"
                        isOpen={showClearOccupationalHealthConfirm}
                        onConfirm={onClearOccupationalHealthConfirm}
                        onCancel={onClearOccupationalHealthCancel}
                    >
                        <p>Removing the reportable medical condition will also remove the occupational health referral and its date. Are you sure you want to continue?</p>
                    </AlertConfirm>

                    <AlertUnsavedData isDirty={isDirty} />

                    <ButtonSave
                        id="save-medical-information"
                        loading={loading}
                        disabled={saving}
                        simpleDisabled={loading || saveBlocked}
                        onClick={saveMedicalInformation}
                    />

                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EmployeeMedical.propTypes = {
    employeeId: PropTypes.string.isRequired
};
